import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "../../../../../utils/cmsAxios";
import CategorySelect from "../../../../../assets/components/CategorySelect";
import { useCategories } from "../../../../../hooks/useCategories";
import { formatKstDateTime } from "../../../../../utils/formatKstDateTime";
import { loadDaumPostcode } from "../../../../../utils/loadDaumPostcode";
import useManualUpload from "./useManualUpload";
import ManualUploadNotice, { MANUAL_UPLOAD_LABELS } from "./ManualUploadNotice";
import {
  FieldLabel,
  InputField,
  ReadField,
  TextAreaField,
  VideoUploadProgress,
} from "./ConciergeDetailFields";

const FILE_BASE_URL = "https://www.wizmarket.ai/uploads";
const MAX_IMAGES = 50;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const MAX_VIDEOS = 12;
const INSTAGRAM_CAROUSEL_MAX_ITEMS = 10;
const MAX_VIDEO_BYTES = 200 * 1024 * 1024;
const MAX_VIDEO_DURATION_SECONDS = 60;
const UPLOAD_TYPES = [
  { value: "SINGLE_IMAGE", label: "이미지 단건" },
  { value: "IMAGE_CAROUSEL", label: "이미지 + 이미지" },
  { value: "MIXED_CAROUSEL", label: "이미지 + 동영상" },
  { value: "REELS", label: "릴스" },
];

function parseExpirationDateTime(value) {
  const raw = String(value || "").trim();
  if (!raw) return { date: "", time: "" };

  const match = raw.replace("T", " ").match(/^(\d{4}-\d{2}-\d{2})(?:\s+(\d{2}:\d{2})(?::(\d{2}))?)?/);
  if (!match) return { date: raw.slice(0, 10), time: "" };

  return {
    date: match[1],
    time: match[2] ? `${match[2]}:${match[3] || "00"}` : "23:59:59",
  };
}

function readVideoMetadata(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const duration = Number(video.duration);
      if (!Number.isFinite(duration) || duration <= 0) {
        URL.revokeObjectURL(url);
        reject(new Error("동영상 재생 시간을 확인할 수 없습니다."));
        return;
      }
      video.onloadedmetadata = null;
      video.onerror = null;
      resolve({ duration, previewUrl: url });
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("동영상 정보를 읽을 수 없습니다."));
    };
    video.src = url;
  });
}

export default function ConciergeDetail({ userMode = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);
  const [terminateLoading, setTerminateLoading] = useState(false);
  const [stopLoading, setStopLoading] = useState(false);
  const [restartLoading, setRestartLoading] = useState(false);
  const manualUpload = useManualUpload(id, !userMode);
  const manualUploadLoading = ["SUBMITTING", "QUEUED", "RUNNING"].includes(manualUpload.phase);
  const manualUploadQueued = manualUpload.blocked;
  const [updateError, setUpdateError] = useState("");

  const [serverImages, setServerImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [removedFileIds, setRemovedFileIds] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [serverVideos, setServerVideos] = useState([]);
  const [newVideos, setNewVideos] = useState([]);
  const [removedVideoIds, setRemovedVideoIds] = useState([]);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [smsSettings, setSmsSettings] = useState({
    selected_template_code: null,
    delivery_mode: "SCHEDULED",
    templates: [],
  });
  const [smsModeSaving, setSmsModeSaving] = useState(false);
  const [smsSettingsLoading, setSmsSettingsLoading] = useState(false);
  const [smsSettingsError, setSmsSettingsError] = useState("");
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [smsDraftCode, setSmsDraftCode] = useState("");
  const [smsDraftTitle, setSmsDraftTitle] = useState("");
  const [smsDraftContent, setSmsDraftContent] = useState("");
  const [smsDraftOriginal, setSmsDraftOriginal] = useState({ title: "", content: "" });
  const [smsTemplateSaving, setSmsTemplateSaving] = useState(false);
  const [smsTemplateSelecting, setSmsTemplateSelecting] = useState(false);

  const [userName, setUserName] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [expirationTime, setExpirationTime] = useState("");
  const [menu1, setMenu1] = useState("");
  const [menu2, setMenu2] = useState("");
  const [menu3, setMenu3] = useState("");
  const [extraNote, setExtraNote] = useState("");
  const [storeName, setStoreName] = useState("");
  const [roadName, setRoadName] = useState("");
  const [detailAddress, setDetailAddress] = useState("");
  const [uploadImageCount, setUploadImageCount] = useState(1);
  const [uploadType, setUploadType] = useState("SINGLE_IMAGE");
  const [uploadVideoCount, setUploadVideoCount] = useState(0);
  const [videoSourceUrl, setVideoSourceUrl] = useState("");
  const [instagramLocation, setInstagramLocation] = useState({
    locationId: "",
    name: "",
    address: "",
  });
  const [locationCandidates, setLocationCandidates] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [locationQuery, setLocationQuery] = useState("");
  const [locationNotice, setLocationNotice] = useState("");
  const [locationRadius, setLocationRadius] = useState(3000);

  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const detailAddressRef = useRef(null);

  const {
    mainCategory, setMainCategory, mainCategories,
    subCategory, setSubCategory, subCategories,
    detailCategory, setDetailCategory, detailCategories,
  } = useCategories();

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const requestUrl = userMode
        ? `${process.env.REACT_APP_FASTAPI_ADS_URL}/concierge/user/my/detail`
        : `${process.env.REACT_APP_FASTAPI_ADS_URL}/select/concierge/detail/${id}`;
      const requestHeaders = userMode
        ? { Authorization: `Bearer ${localStorage.getItem("concierge_cms_access_token") || ""}` }
        : undefined;
      const { data } = await axios.get(requestUrl, { headers: requestHeaders });
      setDetail(data);
      setServerImages(data.images || []);
      setServerVideos(data.videos || []);
    } catch (err) {
      console.error("[admin/concierge/detail] error:", err);
      setError(
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "상세 정보를 불러오는 중 오류가 발생했습니다."
      );
    } finally {
      setLoading(false);
    }
  }, [id, userMode]);

  const fetchSmsSettings = useCallback(async () => {
    if (userMode) return;
    try {
      setSmsSettingsLoading(true);
      setSmsSettingsError("");
      const base = process.env.REACT_APP_FASTAPI_ADS_URL;
      const { data } = await axios.get(`${base}/select/concierge/sms-settings/${id}`);
      setSmsSettings({
        selected_template_code: data?.selected_template_code || null,
        delivery_mode: data?.delivery_mode || "SCHEDULED",
        templates: Array.isArray(data?.templates) ? data.templates : [],
      });
    } catch (err) {
      console.error("[admin/concierge/sms-settings] error:", err);
      setSmsSettingsError(
        err?.response?.data?.detail || "SMS 전송 설정을 불러오지 못했습니다."
      );
    } finally {
      setSmsSettingsLoading(false);
    }
  }, [id, userMode]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    fetchSmsSettings();
  }, [fetchSmsSettings]);

  useEffect(() => {
    if (!previewImage) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setPreviewImage(null);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [previewImage]);

  useEffect(() => {
    if (!previewVideo) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setPreviewVideo(null);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [previewVideo]);

  useEffect(() => {
    if (!detail) return;

    setUserName(detail.user_name || "");
    setUserPhone(detail.phone || "");
    const expiration = parseExpirationDateTime(detail.expiration_date);
    setExpirationDate(expiration.date);
    setExpirationTime(expiration.time);
    setMenu1(detail.menu_1 || "");
    setMenu2(detail.menu_2 || "");
    setMenu3(detail.menu_3 || "");
    setExtraNote(detail.extra_note || "");
    setStoreName(detail.store_name || "");
    setRoadName(detail.road_name || "");
    setDetailAddress(detail.detail_address || "");
    const availableImageCount = Math.min((detail.images || []).length, INSTAGRAM_CAROUSEL_MAX_ITEMS);
    const availableVideoCount = Math.min((detail.videos || []).length, INSTAGRAM_CAROUSEL_MAX_ITEMS);
    const normalizedUploadType = String(
      detail.upload_type || "SINGLE_IMAGE"
    ).trim().toUpperCase();
    setUploadType(normalizedUploadType);
    setUploadImageCount(
      normalizedUploadType === "REELS"
        ? 0
        : Math.max(1, Math.min(Number(detail.upload_image_count) || 1, availableImageCount || 1))
    );
    setUploadVideoCount(
      Math.max(0, Math.min(Number(detail.upload_video_count) || 0, availableVideoCount))
    );
    setVideoSourceUrl(String(detail.reels_source_url || "").trim());
    setInstagramLocation({
      locationId: String(detail.instagram_location_id || "").trim(),
      name: String(detail.instagram_location_name || "").trim(),
      address: String(detail.instagram_location_address || "").trim(),
    });
    setLocationCandidates([]);
    setLocationError("");
    setLocationNotice("");
    setLocationQuery("");

    setMainCategory(detail.main_category_code || "대분류");
    setSubCategory(detail.sub_category_code || "중분류");
    setDetailCategory(detail.detail_category_code || "소분류");

  }, [detail, setMainCategory, setSubCategory, setDetailCategory]);

  const memberStatus = "컨시어지 회원";
  const approveStatus = (() => {
    const s = String(detail?.status || "").toLowerCase();
    if (s === "approved") return "승인";
    if (s === "pending") return "대기";
    if (s === "paused") return "일시정지";
    if (s === "stopped") return "종료";
    return "미승인";
  })();

  const isApprovedStatus =
    detail?.status === "APPROVED" ||
    detail?.status === "approved" ||
    approveStatus === "승인";
  const isPendingStatus =
    String(detail?.status || "").trim().toUpperCase() === "PENDING";
  const isStoppedStatus =
    detail?.status === "STOPPED" ||
    detail?.status === "stopped" ||
    approveStatus === "종료";
  const isPausedStatus =
    detail?.status === "PAUSED" ||
    detail?.status === "paused" ||
    approveStatus === "일시정지";
  const isInactiveStatus = isPausedStatus || isStoppedStatus;

  const categoryCodeOrEmpty = (category) => {
    const value = String(category ?? "").trim();
    return ["", "0", "대분류", "중분류", "소분류"].includes(value) ? "" : value;
  };

  const visibleServerImages = serverImages.filter((img) => !removedFileIds.includes(img.file_id));
  const totalImageCount = visibleServerImages.length + newImages.length;
  const visibleServerVideos = serverVideos.filter((video) => !removedVideoIds.includes(video.video_id));
  const totalVideoCount = visibleServerVideos.length + newVideos.length;
  const normalizedVideoSourceUrl = videoSourceUrl.trim();
  const selectedSmsTemplate = smsSettings.templates.find(
    (template) => template.template_code === smsSettings.selected_template_code
  ) || null;
  const smsDraftDirty =
    smsDraftTitle !== smsDraftOriginal.title ||
    smsDraftContent !== smsDraftOriginal.content;
  const firstSmsImage = visibleServerImages[0] || null;
  const firstSmsImageStoragePath = String(firstSmsImage?.storage_path || "").trim();
  const smsImagePreviewUrl =
    firstSmsImage?.public_url ||
    (firstSmsImageStoragePath ? `${FILE_BASE_URL}/${firstSmsImageStoragePath}` : "") ||
    newImages[0]?.previewUrl ||
    "";
  const firstSmsVideo = visibleServerVideos[0] || null;
  const firstSmsVideoStoragePath = String(firstSmsVideo?.storage_path || "").trim();
  const smsVideoPreviewUrl =
    firstSmsVideo?.public_url ||
    (firstSmsVideoStoragePath ? `${FILE_BASE_URL}/${firstSmsVideoStoragePath}` : "") ||
    newVideos[0]?.previewUrl ||
    normalizedVideoSourceUrl ||
    "";
  const smsPreviewUsesVideo =
    (uploadType === "REELS" || !smsImagePreviewUrl) && Boolean(smsVideoPreviewUrl);
  const isVideoSourceUrlValid = (() => {
    if (!normalizedVideoSourceUrl) return false;
    try {
      const parsed = new URL(normalizedVideoSourceUrl);
      return ["http:", "https:"].includes(parsed.protocol) && Boolean(parsed.hostname);
    } catch {
      return false;
    }
  })();
  const totalVideoSourceCount = totalVideoCount + (isVideoSourceUrlValid ? 1 : 0);
  const hasVideoSource = totalVideoSourceCount >= 1;
  const canAddImage = totalImageCount < MAX_IMAGES;
  const canAddVideo = totalVideoCount < MAX_VIDEOS;
  const maxUploadImageCount =
    uploadType === "REELS"
      ? 0
      : uploadType === "IMAGE_CAROUSEL"
      ? Math.min(totalImageCount, INSTAGRAM_CAROUSEL_MAX_ITEMS)
      : uploadType === "MIXED_CAROUSEL"
        ? Math.min(totalImageCount, Math.max(1, INSTAGRAM_CAROUSEL_MAX_ITEMS - uploadVideoCount))
        : 1;
  const minUploadImageCount =
    uploadType === "REELS" ? 0 : uploadType === "IMAGE_CAROUSEL" ? 2 : 1;
  const maxUploadVideoCount =
    uploadType === "MIXED_CAROUSEL"
      ? Math.min(totalVideoSourceCount, Math.max(0, INSTAGRAM_CAROUSEL_MAX_ITEMS - uploadImageCount))
      : uploadType === "REELS"
        ? (hasVideoSource ? 1 : 0)
        : 0;
  const isUploadConfigurationValid =
    (uploadType === "REELS" || totalImageCount >= 1) &&
    (!normalizedVideoSourceUrl || isVideoSourceUrlValid) &&
    (
      (uploadType === "SINGLE_IMAGE" && uploadImageCount === 1 && uploadVideoCount === 0) ||
      (uploadType === "IMAGE_CAROUSEL" &&
        totalImageCount >= 2 &&
        uploadImageCount >= 2 &&
        uploadImageCount <= INSTAGRAM_CAROUSEL_MAX_ITEMS &&
        uploadVideoCount === 0) ||
      (uploadType === "MIXED_CAROUSEL" &&
        totalVideoSourceCount >= 1 &&
        uploadImageCount >= 1 &&
        uploadVideoCount >= 1 &&
        uploadImageCount + uploadVideoCount <= INSTAGRAM_CAROUSEL_MAX_ITEMS) ||
      (uploadType === "REELS" &&
        uploadImageCount === 0 &&
        hasVideoSource &&
        uploadVideoCount === 1)
    );

  useEffect(() => {
    if (uploadType === "REELS") {
      setUploadImageCount(0);
      return;
    }
    setUploadImageCount((current) =>
      Math.max(
        Math.min(minUploadImageCount, maxUploadImageCount || 1),
        Math.min(Number(current) || 1, maxUploadImageCount || 1)
      )
    );
  }, [uploadType, minUploadImageCount, maxUploadImageCount]);

  useEffect(() => {
    if (uploadType === "SINGLE_IMAGE" || uploadType === "IMAGE_CAROUSEL") {
      setUploadVideoCount(0);
      return;
    }
    if (uploadType === "REELS") {
      setUploadVideoCount(hasVideoSource ? 1 : 0);
      return;
    }
    setUploadVideoCount((current) =>
      Math.max(1, Math.min(Number(current) || 1, maxUploadVideoCount || 1))
    );
  }, [uploadType, totalVideoCount, maxUploadVideoCount, hasVideoSource]);

  const handleUploadTypeChange = (nextType) => {
    setUploadType(nextType);
    if (nextType === "SINGLE_IMAGE") {
      setUploadImageCount(1);
      setUploadVideoCount(0);
    } else if (nextType === "IMAGE_CAROUSEL") {
      setUploadImageCount(Math.min(Math.max(uploadImageCount, 2), totalImageCount, INSTAGRAM_CAROUSEL_MAX_ITEMS));
      setUploadVideoCount(0);
    } else if (nextType === "MIXED_CAROUSEL") {
      setUploadImageCount(Math.min(Math.max(uploadImageCount, 1), totalImageCount, 9));
      setUploadVideoCount(1);
    } else if (nextType === "REELS") {
      setUploadImageCount(0);
      setUploadVideoCount(1);
    }
  };

  const handleSearchInstagramLocations = async () => {
    try {
      setLocationLoading(true);
      setLocationError("");
      setLocationNotice("");
      setLocationCandidates([]);
      const base = process.env.REACT_APP_FASTAPI_ADS_URL;
      const { data } = await axios.get(
        `${base}/select/concierge/instagram-location-candidates/${id}`,
        { params: locationQuery.trim() ? { query: locationQuery.trim() } : {} }
      );
      const items = Array.isArray(data?.items) ? data.items : [];
      setLocationCandidates(items);
      setLocationRadius(Number(data?.search_radius_meters) || 3000);
      if (!items.length) {
        setLocationNotice(
          data?.message || "Meta 검색에서 연결 가능한 위치를 찾지 못했습니다. 검색어를 바꾸거나 Meta 위치 ID로 조회해 주세요. 주변에 위치가 없다는 뜻은 아닙니다."
        );
      }
    } catch (err) {
      console.error("[admin/concierge/instagram-location-candidates] error:", err);
      setLocationCandidates([]);
      setLocationError(
        err?.response?.data?.detail || "Meta 위치 후보를 불러오지 못했습니다."
      );
    } finally {
      setLocationLoading(false);
    }
  };

  const handleSelectInstagramLocation = async (candidate) => {
    try {
      setLocationLoading(true);
      setLocationError("");
      const base = process.env.REACT_APP_FASTAPI_ADS_URL;
      const { data } = await axios.post(
        `${base}/select/concierge/instagram-location/${id}`,
        {
          location_id: candidate.location_id,
        }
      );
      const locationData = data?.location || {};
      setInstagramLocation({
        locationId: String(locationData.location_id || candidate.location_id || ""),
        name: String(locationData.name || candidate.name || ""),
        address: String(locationData.address || candidate.address || ""),
      });
      setLocationCandidates([]);
      setLocationNotice("");
    } catch (err) {
      console.error("[admin/concierge/instagram-location] save error:", err);
      setLocationError(
        err?.response?.data?.detail || "선택한 Meta 위치를 저장하지 못했습니다."
      );
    } finally {
      setLocationLoading(false);
    }
  };

  const handleClearInstagramLocation = async () => {
    if (!window.confirm("자동업로드 위치 태그를 해제하시겠습니까?")) return;
    try {
      setLocationLoading(true);
      setLocationError("");
      const base = process.env.REACT_APP_FASTAPI_ADS_URL;
      await axios.delete(`${base}/select/concierge/instagram-location/${id}`);
      setInstagramLocation({ locationId: "", name: "", address: "" });
      setLocationCandidates([]);
    } catch (err) {
      console.error("[admin/concierge/instagram-location] clear error:", err);
      setLocationError(
        err?.response?.data?.detail || "Meta 위치 태그를 해제하지 못했습니다."
      );
    } finally {
      setLocationLoading(false);
    }
  };

  const handleRemoveServerImage = (fileId) => {
    if (!window.confirm("해당 이미지를 삭제하시겠습니까?")) return;
    setRemovedFileIds((prev) => (prev.includes(fileId) ? prev : [...prev, fileId]));
  };

  const handleRemoveNewImage = (tempId) => {
    const target = newImages.find((img) => img.tempId === tempId);
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
    setNewImages((prev) => prev.filter((img) => img.tempId !== tempId));
  };

  const handleAddImage = () => {
    if (totalImageCount >= MAX_IMAGES) {
      alert(`이미지는 최대 ${MAX_IMAGES}장까지 등록할 수 있습니다.`);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remain = MAX_IMAGES - totalImageCount;
    const validFiles = files.filter((file) => {
      if (file.size > MAX_IMAGE_BYTES) {
        alert(`${file.name}: 이미지 파일은 2MB 이하만 등록할 수 있습니다.`);
        return false;
      }
      return true;
    });
    const slice = validFiles.slice(0, remain);
    const appended = slice.map((file, idx) => ({
      tempId: `${Date.now()}-${idx}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setNewImages((prev) => [...prev, ...appended]);
    e.target.value = "";
  };

  const handleRemoveServerVideo = (videoId) => {
    if (!window.confirm("해당 동영상을 삭제하시겠습니까?")) return;
    setRemovedVideoIds((prev) => (prev.includes(videoId) ? prev : [...prev, videoId]));
  };

  const handleRemoveNewVideo = (tempId) => {
    const target = newVideos.find((video) => video.tempId === tempId);
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
    setNewVideos((prev) => prev.filter((video) => video.tempId !== tempId));
  };

  const updateNewVideoUploadState = (tempId, changes) => {
    setNewVideos((prev) =>
      prev.map((video) => (
        video.tempId === tempId ? { ...video, ...changes } : video
      ))
    );
  };

  const handleAddVideo = () => {
    if (totalVideoCount >= MAX_VIDEOS) {
      alert(`동영상은 최대 ${MAX_VIDEOS}개까지 등록할 수 있습니다.`);
      return;
    }
    videoInputRef.current?.click();
  };

  const handleVideoInputChange = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;

    const remain = MAX_VIDEOS - totalVideoCount;
    const appended = [];
    for (const [index, file] of files.slice(0, remain).entries()) {
      const extension = String(file.name || "").toLowerCase().split(".").pop();
      if (
        extension !== "mp4" ||
        !["video/mp4", "application/octet-stream", ""].includes(file.type)
      ) {
        alert(`${file.name}: MP4 동영상만 등록할 수 있습니다.`);
        continue;
      }
      if (file.size > MAX_VIDEO_BYTES) {
        alert(`${file.name}: 동영상 파일은 최대 200MB까지 등록할 수 있습니다.`);
        continue;
      }
      try {
        const { duration, previewUrl } = await readVideoMetadata(file);
        if (duration > MAX_VIDEO_DURATION_SECONDS) {
          URL.revokeObjectURL(previewUrl);
          continue;
        }
        appended.push({
          tempId: `${Date.now()}-video-${index}`,
          file,
          previewUrl,
          duration,
          uploadStatus: "pending",
          uploadProgress: 0,
          uploadError: "",
        });
      } catch (error) {
        alert(`${file.name}: ${error.message}`);
      }
    }
    if (appended.length) setNewVideos((prev) => [...prev, ...appended]);
  };

  const openPostcodePopup = async () => {
    await loadDaumPostcode();
    new window.daum.Postcode({
      oncomplete: (data) => {
        setRoadName(data.roadAddress || data.jibunAddress || "");
        setTimeout(() => detailAddressRef.current?.focus?.(), 0);
      },
    }).open();
  };

  const loadSmsDraft = (templateCode) => {
    const template = smsSettings.templates.find(
      (item) => item.template_code === templateCode
    );
    if (!template) return;
    const title = String(template.title || "");
    const content = String(template.content || "");
    setSmsDraftCode(template.template_code);
    setSmsDraftTitle(title);
    setSmsDraftContent(content);
    setSmsDraftOriginal({ title, content });
    setSmsSettingsError("");
  };

  const handleOpenSmsModal = () => {
    const initialCode =
      smsSettings.selected_template_code ||
      smsSettings.templates[0]?.template_code ||
      "";
    if (!initialCode) {
      alert("SMS 기본 문구를 불러오지 못했습니다.");
      return;
    }
    loadSmsDraft(initialCode);
    setSmsModalOpen(true);
  };

  const handleChangeSmsDraftTemplate = (templateCode) => {
    if (smsDraftDirty && !window.confirm("저장하지 않은 수정 내용을 취소하시겠습니까?")) {
      return;
    }
    loadSmsDraft(templateCode);
  };

  const handleSaveSmsTemplate = async () => {
    if (!smsDraftTitle.trim() || !smsDraftContent.trim()) {
      alert("SMS 제목과 내용을 모두 입력해 주세요.");
      return;
    }
    try {
      setSmsTemplateSaving(true);
      setSmsSettingsError("");
      const base = process.env.REACT_APP_FASTAPI_ADS_URL;
      const { data } = await axios.post(`${base}/select/concierge/sms-template/${id}`, {
        template_code: smsDraftCode,
        title: smsDraftTitle,
        content: smsDraftContent,
      });
      setSmsSettings(data);
      const saved = data.templates.find(
        (item) => item.template_code === smsDraftCode
      );
      const savedTitle = String(saved?.title || smsDraftTitle);
      const savedContent = String(saved?.content || smsDraftContent);
      setSmsDraftTitle(savedTitle);
      setSmsDraftContent(savedContent);
      setSmsDraftOriginal({ title: savedTitle, content: savedContent });
      alert("SMS 공통 문구가 저장되어 모든 매장에 적용되었습니다.");
    } catch (err) {
      console.error("[admin/concierge/sms-template] save error:", err);
      setSmsSettingsError(
        err?.response?.data?.detail || "SMS 문구를 저장하지 못했습니다."
      );
    } finally {
      setSmsTemplateSaving(false);
    }
  };

  const handleSelectSmsTemplate = async () => {
    if (smsDraftDirty) {
      alert("수정한 문구를 먼저 저장해 주세요.");
      return;
    }
    try {
      setSmsTemplateSelecting(true);
      setSmsSettingsError("");
      const base = process.env.REACT_APP_FASTAPI_ADS_URL;
      const { data } = await axios.post(`${base}/select/concierge/sms-selection/${id}`, {
        template_code: smsDraftCode,
      });
      setSmsSettings(data);
      setSmsModalOpen(false);
    } catch (err) {
      console.error("[admin/concierge/sms-template] select error:", err);
      setSmsSettingsError(
        err?.response?.data?.detail || "SMS 문구를 선택하지 못했습니다."
      );
    } finally {
      setSmsTemplateSelecting(false);
    }
  };

  const handleSmsModeChange = async (deliveryMode) => {
    if (smsModeSaving || deliveryMode === smsSettings.delivery_mode) return;
    try {
      setSmsModeSaving(true);
      setSmsSettingsError("");
      const base = process.env.REACT_APP_FASTAPI_ADS_URL;
      const { data } = await axios.post(`${base}/select/concierge/sms-delivery-mode/${id}`, {
        delivery_mode: deliveryMode,
      });
      setSmsSettings(data);
    } catch (err) {
      setSmsSettingsError(err?.response?.data?.detail || "SMS 발송 방식을 저장하지 못했습니다.");
    } finally {
      setSmsModeSaving(false);
    }
  };

  const handleCloseSmsModal = () => {
    if (smsDraftDirty && !window.confirm("저장하지 않은 수정 내용을 취소하시겠습니까?")) {
      return;
    }
    setSmsModalOpen(false);
  };

  const handleSaveSelected = async ({ approve = false } = {}) => {
    if (!detail) return;
    if (normalizedVideoSourceUrl && !isVideoSourceUrlValid) {
      alert("동영상 원본 URL은 외부에서 접근 가능한 HTTP 또는 HTTPS 주소로 입력해 주세요.");
      return;
    }
    if (uploadType === "SINGLE_IMAGE" && totalImageCount < 1) {
      alert("이미지 단건 업로드를 위해 이미지를 1개 이상 추가해 주세요.");
      return;
    }
    if (uploadType === "IMAGE_CAROUSEL" && totalImageCount < 2) {
      alert("이미지 + 이미지 업로드를 위해 이미지를 2개 이상 추가해 주세요.");
      return;
    }
    if (uploadType === "MIXED_CAROUSEL" && totalImageCount < 1) {
      alert("이미지 + 동영상 업로드를 위해 이미지를 1개 이상 추가해 주세요.");
      return;
    }
    if (uploadType === "MIXED_CAROUSEL" && !hasVideoSource) {
      alert("이미지 + 동영상 업로드를 위해 동영상을 추가하거나 동영상 원본 URL을 입력해 주세요.");
      return;
    }
    if (uploadType === "REELS" && !hasVideoSource) {
      alert("릴스 업로드를 위해 동영상을 추가하거나 동영상 원본 URL을 입력해 주세요.");
      return;
    }
    if (!isUploadConfigurationValid) {
      alert("업로드 타입에 맞게 이미지와 동영상 개수를 선택해 주세요.");
      return;
    }

    try {
      setUpdateLoading(true);
      setUpdateError("");

      const base = process.env.REACT_APP_FASTAPI_ADS_URL;
      const videoUploadUrl = userMode
        ? `${base}/concierge/user/my/video`
        : `${base}/concierge/${id}/video`;
      const videoUploadHeaders = userMode
        ? { Authorization: `Bearer ${localStorage.getItem("concierge_cms_access_token") || ""}` }
        : {};

      for (const video of newVideos.filter((item) => item.uploadStatus !== "completed")) {
        updateNewVideoUploadState(video.tempId, {
          uploadStatus: "uploading",
          uploadProgress: 0,
          uploadError: "",
        });
        const videoFormData = new FormData();
        videoFormData.append("video", video.file);
        removedVideoIds.forEach((videoId) => {
          videoFormData.append("removed_video_ids", String(videoId));
        });
        try {
          await axios.post(videoUploadUrl, videoFormData, {
            headers: {
              "Content-Type": "multipart/form-data",
              ...videoUploadHeaders,
            },
            onUploadProgress: (event) => {
              if (!event.total) return;
              updateNewVideoUploadState(video.tempId, {
                uploadProgress: Math.min(
                  99,
                  Math.round((event.loaded * 100) / event.total)
                ),
              });
            },
          });
          updateNewVideoUploadState(video.tempId, {
            uploadStatus: "completed",
            uploadProgress: 100,
          });
        } catch (videoError) {
          const reason =
            videoError?.response?.data?.detail ||
            videoError?.response?.data?.message ||
            "동영상 업로드 중 오류가 발생했습니다.";
          updateNewVideoUploadState(video.tempId, {
            uploadStatus: "failed",
            uploadError: reason,
          });
          throw new Error(`${video.file.name}: ${reason}`);
        }
      }

      const formData = new FormData();
      formData.append("status", approve ? "APPROVED" : (detail.status || "PENDING"));
      formData.append("store_name", storeName || "");
      formData.append("road_name", roadName || "");
      formData.append("detail_address", detailAddress || "");
      formData.append("user_name", userName || "");
      formData.append("phone", userPhone || "");
      formData.append(
        "expiration_date",
        expirationDate ? `${expirationDate} ${expirationTime || "23:59:59"}` : ""
      );
      formData.append("memo", detail.memo || "");
      formData.append("extra_note", extraNote || "");
      formData.append(
        "upload_image_count",
        String(uploadType === "REELS" ? 0 : (uploadImageCount || 1))
      );
      formData.append("upload_type", uploadType);
      formData.append("upload_video_count", String(uploadVideoCount || 0));
      formData.append("reels_source_url", normalizedVideoSourceUrl);
      formData.append(
        "clear_reels_source_url",
        normalizedVideoSourceUrl ? "false" : "true"
      );
      formData.append("menu_1", menu1 || "");
      formData.append("menu_2", menu2 || "");
      formData.append("menu_3", menu3 || "");

      if (userMode) {
        formData.append("store_business_number", detail.store_business_number || "");
        formData.append("main_category_code", categoryCodeOrEmpty(detail.main_category_code));
        formData.append("sub_category_code", categoryCodeOrEmpty(detail.sub_category_code));
        formData.append("detail_category_code", categoryCodeOrEmpty(detail.detail_category_code));
      } else {
        formData.append("main_category_code", categoryCodeOrEmpty(mainCategory));
        formData.append("sub_category_code", categoryCodeOrEmpty(subCategory));
        formData.append("detail_category_code", categoryCodeOrEmpty(detailCategory));
      }

      removedFileIds.forEach((fid) => formData.append("removed_file_ids", String(fid)));
      newImages.forEach((img) => formData.append("new_files", img.file));
      removedVideoIds.forEach((videoId) => formData.append("removed_video_ids", String(videoId)));

      const url = userMode
        ? `${base}/concierge/user/my/update`
        : approve
          ? `${base}/concierge/approve/${id}`
          : `${base}/concierge/update/${id}`;

      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(userMode
            ? { Authorization: `Bearer ${localStorage.getItem("concierge_cms_access_token") || ""}` }
            : {}),
        },
      });
      if (response?.data?.success !== true) {
        throw new Error(
          response?.data?.detail ||
          response?.data?.message ||
          response?.data?.messeage ||
          "저장 처리 결과를 확인하지 못했습니다."
        );
      }

      alert(approve ? "승인 및 정보 저장이 완료되었습니다." : "수정이 완료되었습니다.");
      if (userMode) {
        window.location.reload();
      } else if (approve) {
        handleBackToList();
      } else {
        setNewImages([]);
        setRemovedFileIds([]);
        setNewVideos([]);
        setRemovedVideoIds([]);
        navigate(`${location.pathname}${location.search}`, {
          replace: true,
          state: location.state,
        });
        await fetchDetail();
        await fetchSmsSettings();
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    } catch (err) {
      console.error("[approve concierge] error:", err);
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        (approve ? "승인 처리 중 오류가 발생했습니다." : "수정 처리 중 오류가 발생했습니다.");
      setUpdateError(message);
      if (err?.response?.status === 409) {
        alert(message);
      }
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleManualUpload = async () => {
    if (!detail || userMode || manualUpload.blocked) return;

    const savedUploadType = String(
      detail.upload_type || "SINGLE_IMAGE"
    ).trim().toUpperCase();
    const savedUploadTypeLabel =
      UPLOAD_TYPES.find((item) => item.value === savedUploadType)?.label ||
      savedUploadType;
    const savedImageCount =
      savedUploadType === "REELS"
        ? 0
        : Math.max(0, Number(detail.upload_image_count) || 0);
    const savedVideoCount = Math.max(
      0,
      Number(detail.upload_video_count) || 0
    );
    const savedAddress = [
      String(detail.road_name || "").trim(),
      String(detail.detail_address || "").trim(),
    ].filter(Boolean).join(" ");

    const confirmed = window.confirm(
      [
        "저장된 매장 설정으로 인스타 수동업로드를 시작하시겠습니까?",
        "",
        `매장명: ${String(detail.store_name || "-").trim() || "-"}`,
        `주소: ${savedAddress || "-"}`,
        "업로드 계정: 실행 시 main 게시 한도 확인 후 필요하면 special 사용",
        `업로드 타입: ${savedUploadTypeLabel}`,
        `이미지 개수: ${savedImageCount}장`,
        `동영상 개수: ${savedVideoCount}개`,
        "",
        "현재 화면의 미저장 변경사항은 반영되지 않습니다.",
        "확인을 누르면 백그라운드 업로드가 시작됩니다.",
      ].join("\n")
    );
    if (!confirmed) return;

    setUpdateError("");
    const data = await manualUpload.start();
    if (data) alert(data.message || "수동업로드가 대기열에 등록되었습니다.");
  };

  const handleRestartSelected = async () => {
    if (!detail || !isInactiveStatus) return;
    const inactiveLabel = isPausedStatus ? "일시정지" : "종료";
    if (!window.confirm(`현재 ${inactiveLabel} 상태인 매장입니다.\n재시작 하겠습니까?`)) return;

    try {
      setRestartLoading(true);
      setUpdateError("");

      const base = process.env.REACT_APP_FASTAPI_ADS_URL;
      const url = userMode
        ? `${base}/concierge/user/my/restart`
        : `${base}/concierge/restart/${id}`;

      const response = await axios.post(url, null, {
        headers: {
          ...(userMode
            ? { Authorization: `Bearer ${localStorage.getItem("concierge_cms_access_token") || ""}` }
            : {}),
        },
      });

      const restoredStatus = String(response?.data?.status || "").toUpperCase();
      if (response?.data?.expiration_reset) {
        alert("종료일이 지나 종료일을 초기화하고 재시작했습니다.");
      }
      alert(
        restoredStatus === "APPROVED"
          ? "컨시어지가 승인 상태로 복원되었습니다."
          : "컨시어지가 대기 상태로 복원되었습니다."
      );
      window.location.reload();
    } catch (err) {
      console.error("[restart concierge] error:", err);
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "재시작 처리 중 오류가 발생했습니다.";
      setUpdateError(message);
      if (err?.response?.status === 409) {
        alert(message);
      }
    } finally {
      setRestartLoading(false);
    }
  };

  const handleStopSelected = async () => {
    if (!detail || !isApprovedStatus) return;
    if (!window.confirm("해당 매장을 일시정지하시겠습니까? 대기 중인 자동업로드도 함께 일시정지됩니다.")) return;

    try {
      setStopLoading(true);
      setUpdateError("");

      const base = process.env.REACT_APP_FASTAPI_ADS_URL;
      const url = userMode
        ? `${base}/concierge/user/my/pause`
        : `${base}/concierge/pause/${id}`;

      await axios.post(url, null, {
        headers: userMode
          ? { Authorization: `Bearer ${localStorage.getItem("concierge_cms_access_token") || ""}` }
          : undefined,
      });

      alert("컨시어지가 일시정지되었습니다.");
      window.location.reload();
    } catch (err) {
      console.error("[stop concierge] error:", err);
      setUpdateError(
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "일시정지 처리 중 오류가 발생했습니다."
      );
    } finally {
      setStopLoading(false);
    }
  };

  const handleTerminateSelected = async () => {
    if (!detail || !isApprovedStatus) return;
    if (
      !window.confirm(
        "해당 매장을 지금 종료하시겠습니까? 대기 중인 자동업로드도 함께 종료됩니다."
      )
    ) {
      return;
    }

    try {
      setTerminateLoading(true);
      setUpdateError("");

      const base = process.env.REACT_APP_FASTAPI_ADS_URL;
      const url = userMode
        ? `${base}/concierge/user/my/stop`
        : `${base}/concierge/stop/${id}`;
      const response = await axios.post(url, null, {
        headers: userMode
          ? { Authorization: `Bearer ${localStorage.getItem("concierge_cms_access_token") || ""}` }
          : undefined,
      });

      const stoppedAt = String(response?.data?.expiration_date || "").trim();
      alert(
        stoppedAt
          ? `컨시어지가 종료되었습니다.\n종료 일시: ${stoppedAt}`
          : "컨시어지가 종료되었습니다."
      );
      window.location.reload();
    } catch (err) {
      console.error("[terminate concierge] error:", err);
      setUpdateError(
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "종료 처리 중 오류가 발생했습니다."
      );
    } finally {
      setTerminateLoading(false);
    }
  };

  const handleBackToList = () => {
    if (userMode) {
      navigate("/concierge/manage/my");
      return;
    }

    const returnTo =
      location.state?.returnTo ||
      new URLSearchParams(location.search).get("return_to") ||
      sessionStorage.getItem("concierge_list_return_to");
    if (returnTo) {
      sessionStorage.removeItem("concierge_list_return_to");
      if (location.state?.returnSource === "history") {
        navigate(returnTo, {
          replace: true,
          state: location.state?.returnState || null,
        });
        return;
      }
      window.location.assign(returnTo);
      return;
    }

    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-sm text-neutral-500">로딩 중입니다...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center gap-3">
        <div className="text-sm text-red-600">{error}</div>
        <button
          type="button"
          className="px-4 py-2 rounded border border-neutral-300 text-sm"
          onClick={handleBackToList}
        >
          목록
        </button>
      </div>
    );
  }

  if (!detail) return null;

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="mx-auto max-w-[1280px] py-8">
        <main className="pt-12 flex flex-col p-4 pb-28 overflow-x-hidden w-full">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">
              {userMode ? "내 컨시어지" : "컨시어지 목록 > 상세(수정)"}
            </h2>
          </div>

          {!userMode && <ManualUploadNotice upload={manualUpload} />}
          <h2 className="text-sm font-semibold text-neutral-900 mb-3">기본 정보</h2>
          <div className="bg-white shadow-sm border border-neutral-100 p-6 space-y-8">
            <section>
              <div className="border border-neutral-200 bg-neutral-50/60 px-5 py-4 text-sm">
                <div className="divide-y divide-neutral-100">
                  <div className="flex w-full py-2">
                    <div className="flex w-1/2">
                      <FieldLabel>회원 상태</FieldLabel>
                      <ReadField>{memberStatus}</ReadField>
                    </div>
                    <div className="flex w-1/2">
                      <FieldLabel>서비스 상태</FieldLabel>
                      <ReadField>{approveStatus}</ReadField>
                    </div>
                  </div>

                  <div className="flex w-full py-2">
                    <div className="flex w-1/2">
                      <FieldLabel>이름</FieldLabel>
                      <InputField value={userName} onChange={(e) => setUserName(e.target.value)} />
                    </div>
                    <div className="flex w-1/2">
                      <FieldLabel>휴대폰 번호</FieldLabel>
                      <InputField value={userPhone} onChange={(e) => setUserPhone(e.target.value)} />
                    </div>
                  </div>

                  <div className="flex w-full py-2">
                    <div className="flex w-1/2">
                      <FieldLabel>종료 날짜</FieldLabel>
                      {!userMode ? (
                        <InputField
                          type="date"
                          value={expirationDate}
                          disabled={isPendingStatus}
                          onChange={(e) => {
                            const nextDate = e.target.value;
                            setExpirationDate(nextDate);
                            setExpirationTime(nextDate ? "23:59:59" : "");
                          }}
                        />
                      ) : (
                        <ReadField>{expirationDate || "-"}</ReadField>
                      )}
                    </div>
                    <div className="flex w-1/2">
                      <FieldLabel>종료 시간</FieldLabel>
                      <ReadField>{expirationTime || "-"}</ReadField>
                    </div>
                  </div>

                  <div className="flex w-full py-2">
                    <div className="flex w-1/2">
                      <FieldLabel>신청 일</FieldLabel>
                      <ReadField>{formatKstDateTime(detail.created_at)}</ReadField>
                    </div>
                    <div className="w-1/2" />
                  </div>

                  <div className="flex w-full py-2">
                    <div className="flex w-1/2">
                      <FieldLabel>승인 일시</FieldLabel>
                      <ReadField>{formatKstDateTime(detail.approved_at)}</ReadField>
                    </div>
                    <div className="w-1/2" />
                  </div>

                  {!userMode && (
                    <>
                      <div className="flex w-full py-2">
                        <div className="flex w-1/2">
                          <FieldLabel>업로드 이미지 개수</FieldLabel>
                          <div className="flex-1 h-9 flex items-center bg-white border border-neutral-200 rounded-md">
                            <select
                              value={uploadImageCount}
                              onChange={(e) => setUploadImageCount(Number(e.target.value))}
                              disabled={
                                totalImageCount < 1 ||
                                uploadType === "SINGLE_IMAGE" ||
                                uploadType === "REELS"
                              }
                              className="w-full h-full px-3 text-sm text-black bg-transparent outline-none disabled:bg-neutral-100"
                            >
                              {Array.from(
                                { length: Math.max(0, maxUploadImageCount - minUploadImageCount + 1) },
                                (_, index) => minUploadImageCount + index
                              ).map((count) => (
                                <option key={count} value={count}>
                                  {count}장
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="flex w-full py-2">
                        <div className="flex w-1/2">
                          <FieldLabel>업로드 타입</FieldLabel>
                          <div className="flex-1 h-9 flex items-center bg-white border border-neutral-200 rounded-md">
                            <select
                              value={uploadType}
                              onChange={(e) => handleUploadTypeChange(e.target.value)}
                              className="w-full h-full px-3 text-sm text-black bg-transparent outline-none"
                            >
                              {UPLOAD_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="flex w-1/2">
                          <FieldLabel>업로드 동영상 개수</FieldLabel>
                          <div className="flex-1 h-9 flex items-center bg-white border border-neutral-200 rounded-md">
                            <select
                              value={uploadVideoCount}
                              onChange={(e) => setUploadVideoCount(Number(e.target.value))}
                              disabled={uploadType !== "MIXED_CAROUSEL" || totalVideoSourceCount < 1}
                              className="w-full h-full px-3 text-sm text-black bg-transparent outline-none disabled:bg-neutral-100"
                            >
                              {uploadType === "MIXED_CAROUSEL" ? (
                                Array.from({ length: maxUploadVideoCount }, (_, index) => index + 1).map((count) => (
                                  <option key={count} value={count}>
                                    {count}개
                                  </option>
                                ))
                              ) : (
                                <option value={uploadType === "REELS" && hasVideoSource ? 1 : 0}>
                                  {uploadType === "REELS" && hasVideoSource ? "1개" : "0개"}
                                </option>
                              )}
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="flex w-full py-2 items-start">
                        <FieldLabel>동영상 원본 URL</FieldLabel>
                        <div className="flex-1">
                          <input
                            type="url"
                            value={videoSourceUrl}
                            onChange={(e) => setVideoSourceUrl(e.target.value)}
                            placeholder="https://external-media.example.com/store/reels.mp4"
                            maxLength={2048}
                            className="w-full h-9 px-3 text-sm text-black bg-white border border-neutral-200 rounded-md outline-none"
                          />
                          <p className="mt-1 text-xs text-neutral-500">
                            외부 서버에서 Meta가 직접 접근할 수 있는 공개 HTTP 또는 HTTPS 동영상 URL을 입력해 주세요.
                          </p>
                        </div>
                      </div>

                      <div className="flex w-full py-2 items-start">
                        <FieldLabel>게시물 위치</FieldLabel>
                        <div className="flex-1">
                          <div className="flex min-h-9 items-center gap-2">
                            <div className="min-w-0 flex-1 rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-black">
                              {instagramLocation.locationId ? (
                                <>
                                  <div className="font-medium">
                                    {instagramLocation.name || "이름 없는 Meta 위치"}
                                  </div>
                                  <div className="mt-0.5 text-xs text-neutral-500">
                                    {instagramLocation.address || "주소 정보 없음"}
                                    <span className="ml-2 text-neutral-400">
                                      ID {instagramLocation.locationId}
                                    </span>
                                  </div>
                                </>
                              ) : (
                                <span className="text-neutral-400">위치 미설정</span>
                              )}
                            </div> 
                            <button
                              type="button"
                              onClick={handleSearchInstagramLocations}
                              disabled={locationLoading}
                              className="h-9 shrink-0 rounded-md border border-blue-500 bg-blue-50 px-3 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {locationLoading ? "조회 중..." : "위치 연동"}
                            </button>
                            {instagramLocation.locationId && (
                              <button
                                type="button"
                                onClick={handleClearInstagramLocation}
                                disabled={locationLoading}
                                className="h-9 shrink-0 rounded-md border border-neutral-300 bg-white px-3 text-xs text-neutral-600 hover:bg-neutral-50 disabled:opacity-60"
                              >
                                해제
                              </button>
                            )}
                          </div>

                          {locationError && (
                            <p className="mt-2 text-xs text-red-600">{locationError}</p>
                          )}
                          <div className="mt-2">
                            <input
                              aria-label="위치 검색어 또는 Meta 위치 ID"
                              type="text"
                              value={locationQuery}
                              onChange={(e) => setLocationQuery(e.target.value)}
                              maxLength={100}
                              placeholder="매장명·지역명(영문 가능) 또는 Meta 위치 ID"
                              className="w-full h-9 rounded-md border border-neutral-200 px-3 text-sm"
                            />
                            <p className="mt-1 text-xs text-neutral-500">
                              빈칸으로 조회하면 저장된 매장명과 주소의 동·읍·면 및 영문 지역명으로 검색합니다. 주변 장소는 해당 매장과 다를 수 있으니 이름·주소를 확인하고 선택해 주세요.
                            </p>
                          </div>
                          {locationNotice && (
                            <p role="status" className="mt-2 text-xs text-neutral-600">{locationNotice}</p>
                          )}

                          {locationCandidates.length > 0 && (
                            <div className="mt-3 overflow-hidden rounded-lg border border-neutral-200 bg-white">
                              <div className="border-b border-neutral-200 bg-neutral-50 px-3 py-2 text-xs font-semibold text-neutral-700">
                                저장된 매장 주소 기준 {locationRadius.toLocaleString()}m 내 Meta 위치 후보 (최대 5개)
                              </div>
                              <div className="max-h-72 divide-y divide-neutral-100 overflow-y-auto">
                                {locationCandidates.map((candidate) => (
                                  <button
                                    key={candidate.location_id}
                                    type="button"
                                    onClick={() => handleSelectInstagramLocation(candidate)}
                                    disabled={locationLoading}
                                    className="flex w-full items-center gap-3 px-3 py-3 text-left hover:bg-blue-50 disabled:opacity-60"
                                  >
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-medium text-neutral-900">
                                        {candidate.name || "이름 없는 Meta 위치"}
                                        <span className="ml-2 rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-normal text-neutral-500">
                                          {candidate.match_type === "STORE"
                                            ? "매장 일치"
                                            : candidate.match_type === "GEOGRAPHIC"
                                            ? "역/주소 일치"
                                            : candidate.match_type === "MANUAL"
                                            ? "ID 직접 조회"
                                            : "주변 위치"}
                                        </span>
                                      </div>
                                      <div className="mt-0.5 text-xs text-neutral-500">
                                        {candidate.address || "주소 정보 없음"}
                                      </div>
                                      <div className="mt-1 text-[11px] text-neutral-400">
                                        ID {candidate.location_id}
                                        {Number.isFinite(candidate.distance_meters) && ` · 매장에서 ${candidate.distance_meters.toLocaleString()}m`}
                                      </div>
                                    </div>
                                    <div className="shrink-0 text-xs font-medium text-blue-700">
                                      {Number.isFinite(candidate.distance_meters)
                                        ? `${candidate.distance_meters.toLocaleString()}m`
                                        : "거리 미확인"}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          <p className="mt-1 text-xs text-neutral-500">
                            가까운 순으로 표시되며 주변 업체도 포함될 수 있습니다. 장소명과 주소를 확인한 뒤 선택해 주세요.
                          </p>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex w-full py-2 items-start">
                    <FieldLabel>기타</FieldLabel>
                    <TextAreaField value={extraNote} onChange={(e) => setExtraNote(e.target.value)} />
                  </div>
                </div>
              </div>
            </section>
          </div>

          <h2 className="text-sm font-semibold text-neutral-900 py-3">업종 정보</h2>
          <div className="bg-white shadow-sm border border-neutral-100 p-6 space-y-8">
            <section>
              <div className="border border-neutral-200 bg-neutral-50/60 px-5 py-4 text-sm">
                <div className="divide-y divide-neutral-100">
                  <div className="flex w-full py-2">
                    <div className="flex w-1/2">
                      <FieldLabel>가게 이름</FieldLabel>
                      <InputField value={storeName} onChange={(e) => setStoreName(e.target.value)} />
                    </div>
                    <div className="w-1/2" />
                  </div>

                  <div className="flex w-full py-2">
                    <div className="flex w-1/2">
                      <FieldLabel>주소</FieldLabel>
                      <div className="flex flex-1 gap-2">
                        <div className="flex-1 h-9 flex items-center bg-white border border-neutral-200 rounded-md">
                          <input
                            type="text"
                            value={roadName}
                            onChange={(e) => setRoadName(e.target.value)}
                            onClick={openPostcodePopup}
                            placeholder="-"
                            className="w-full h-full px-3 text-sm text-black bg-transparent outline-none"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={openPostcodePopup}
                          className="h-9 shrink-0 rounded-md border border-neutral-300 bg-white px-3 text-xs text-neutral-700 hover:bg-neutral-50"
                        >
                          주소 검색
                        </button>
                      </div>
                    </div>
                    <div className="flex w-1/2">
                      <FieldLabel>상세 주소</FieldLabel>
                      <div className="flex-1 h-9 flex items-center bg-white border border-neutral-200 rounded-md">
                        <input
                          ref={detailAddressRef}
                          type="text"
                          value={detailAddress}
                          onChange={(e) => setDetailAddress(e.target.value)}
                          placeholder="-"
                          className="w-full h-full px-3 text-sm text-black bg-transparent outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex w-full py-2">
                    <div className="flex w-1/2">
                      <FieldLabel>업종</FieldLabel>
                      <div
                        className={
                          "flex-1 flex items-center bg-white text-sm text-black" +
                          (userMode && isApprovedStatus ? " pointer-events-none" : "")
                        }
                      >
                        <CategorySelect
                          mainCategory={mainCategory}
                          setMainCategory={setMainCategory}
                          mainCategories={mainCategories}
                          subCategory={subCategory}
                          setSubCategory={setSubCategory}
                          subCategories={subCategories}
                          detailCategory={detailCategory}
                          setDetailCategory={setDetailCategory}
                          detailCategories={detailCategories}
                        />
                      </div>
                    </div>
                    <div className="w-1/2" />
                  </div>

                  <div className="flex w-full py-2">
                    <div className="flex w-full">
                      <FieldLabel>주요 메뉴</FieldLabel>
                      <div className="flex-1 grid grid-cols-1 gap-4 bg-white md:grid-cols-3">
                        {[menu1, menu2, menu3].map((value, idx) => (
                          <div key={idx} className="flex flex-col gap-1">
                            <div className="text-xs text-[#4B5563]">주요 메뉴 {idx + 1}.</div>
                            <div className="h-9 flex items-center rounded-md border border-neutral-200 bg-white">
                              <input
                                type="text"
                                value={value}
                                onChange={(e) => [setMenu1, setMenu2, setMenu3][idx](e.target.value)}
                                placeholder="-"
                                className="w-full h-full bg-transparent px-3 text-sm text-black outline-none"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <h2 className="text-sm font-semibold text-neutral-900 py-3">신청 이미지</h2>
          <div className="bg-white shadow-sm border border-neutral-100 p-6 space-y-8">
            <section>
              <div className="mb-3 text-xs text-neutral-500">
                JPG/PNG 권장, 파일당 최대 2MB, 최대 {MAX_IMAGES}개 · 현재 {totalImageCount}개
              </div>

              <div className="rounded-xl border border-neutral-200 bg-neutral-50/60 px-5 py-4">
                <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
                  {visibleServerImages.map((img) => {
                    const storagePath = String(img.storage_path || "").trim();
                    const url =
                      img.public_url ||
                      (storagePath
                        ? (FILE_BASE_URL ? `${FILE_BASE_URL}/${storagePath}` : storagePath)
                        : "");

                    return (
                      <div key={img.file_id} className="relative aspect-square overflow-hidden rounded-xl bg-neutral-100">
                        <button
                          type="button"
                          onClick={() => setPreviewImage({
                            url,
                            alt: img.original_name || "concierge image",
                          })}
                          className="block h-full w-full cursor-zoom-in"
                          aria-label={`${img.original_name || "신청 이미지"} 원본 보기`}
                        >
                          <img
                            src={url}
                            alt={img.original_name || "concierge image"}
                            className="h-full w-full object-cover"
                          />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveServerImage(img.file_id)}
                          className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs text-white"
                        >
                          X
                        </button>
                      </div>
                    );
                  })}

                  {newImages.map((img) => (
                    <div key={img.tempId} className="relative aspect-square overflow-hidden rounded-xl bg-neutral-100">
                      <button
                        type="button"
                        onClick={() => setPreviewImage({ url: img.previewUrl, alt: img.file.name })}
                        className="block h-full w-full cursor-zoom-in"
                        aria-label={`${img.file.name} 원본 보기`}
                      >
                        <img src={img.previewUrl} alt={img.file.name} className="h-full w-full object-cover" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveNewImage(img.tempId)}
                        className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs text-white"
                      >
                        X
                      </button>
                    </div>
                  ))}

                  {canAddImage && (
                    <button
                      type="button"
                      onClick={handleAddImage}
                      aria-label="신청 이미지 추가"
                      className="aspect-square rounded-xl border border-dashed border-neutral-300 bg-white text-xs text-neutral-400 hover:border-violet-400 hover:text-violet-500"
                    >
                      <div className="mb-1 text-2xl">+</div>
                      <div>이미지 추가</div>
                    </button>
                  )}
                </div>
              </div>
            </section>
          </div>

          <h2 className="text-sm font-semibold text-neutral-900 py-3">신청 동영상</h2>
          <div className="bg-white shadow-sm border border-neutral-100 p-6 space-y-8">
            <section>
              <div className="mb-3 text-xs text-neutral-500">
                MP4만 가능, 파일당 최대 200MB, 최대 60초, 최대 {MAX_VIDEOS}개 · 현재 {totalVideoCount}개
              </div>

              <div className="rounded-xl border border-neutral-200 bg-neutral-50/60 px-5 py-4">
                <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
                  {visibleServerVideos.map((video) => {
                    const storagePath = String(video.storage_path || "").trim();
                    const url =
                      video.public_url ||
                      (storagePath
                        ? (FILE_BASE_URL ? `${FILE_BASE_URL}/${storagePath}` : storagePath)
                        : "");

                    return (
                      <div key={video.video_id} className="relative aspect-square overflow-hidden rounded-xl bg-black">
                        <button
                          type="button"
                          onClick={() => setPreviewVideo({
                            url,
                            name: video.original_name || "신청 동영상",
                          })}
                          className="block h-full w-full cursor-pointer"
                          aria-label={`${video.original_name || "신청 동영상"} 원본 보기`}
                        >
                          <video
                            src={url}
                            muted
                            preload="metadata"
                            className="h-full w-full object-cover"
                          />
                          <span className="absolute bottom-1.5 left-1.5 rounded bg-black/65 px-1.5 py-0.5 text-[10px] text-white">
                            {Math.round(Number(video.duration_seconds) || 0)}초
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveServerVideo(video.video_id)}
                          className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs text-white"
                        >
                          X
                        </button>
                      </div>
                    );
                  })}

                  {newVideos.map((video) => (
                    <div key={video.tempId}>
                      <div className="relative aspect-square overflow-hidden rounded-xl bg-black">
                        <button
                          type="button"
                          onClick={() => setPreviewVideo({ url: video.previewUrl, name: video.file.name })}
                          className="block h-full w-full cursor-pointer"
                          aria-label={`${video.file.name} 원본 보기`}
                        >
                          <video
                            src={video.previewUrl}
                            muted
                            preload="metadata"
                            className="h-full w-full object-cover"
                          />
                          <span className="absolute bottom-1.5 left-1.5 rounded bg-black/65 px-1.5 py-0.5 text-[10px] text-white">
                            {Math.round(video.duration)}초
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveNewVideo(video.tempId)}
                          disabled={video.uploadStatus === "uploading" || video.uploadStatus === "completed"}
                          className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs text-white disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          X
                        </button>
                      </div>
                      <VideoUploadProgress video={video} />
                    </div>
                  ))}

                  {canAddVideo && (
                    <button
                      type="button"
                      onClick={handleAddVideo}
                      aria-label="신청 동영상 추가"
                      className="aspect-square rounded-xl border border-dashed border-neutral-300 bg-white text-xs text-neutral-400 hover:border-violet-400 hover:text-violet-500"
                    >
                      <div className="mb-1 text-2xl">+</div>
                      <div>동영상 추가</div>
                    </button>
                  )}
                </div>
              </div>
            </section>
          </div>

          {!userMode && (
            <>
              <h2 className="py-3 text-sm font-semibold text-neutral-900">SMS 전송</h2>
              <div className="space-y-5 border border-neutral-100 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-neutral-900">
                      업로드 완료 MMS 문구
                    </div>
                    <div className="mt-1 text-xs text-neutral-500">
                      문구를 선택하지 않으면 자동·수동 업로드 완료 문자를 발송하지 않습니다.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenSmsModal}
                    disabled={smsSettingsLoading}
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-violet-500 px-3 py-2 text-xs font-medium text-violet-600 hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span className="text-base leading-none">+</span>
                    <span>{smsSettingsLoading ? "불러오는 중" : "불러오기"}</span>
                  </button>
                </div>

                {smsSettingsError && (
                  <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">
                    {smsSettingsError}
                  </div>
                )}

                <div className="cms-sms-field-label flex flex-wrap items-center gap-4 text-sm text-neutral-900">
                  <span className="cms-sms-field-label font-medium">업로드 완료 문자 발송</span>
                  {[
                    ["SCHEDULED", "예약"],
                    ["IMMEDIATE", "즉시"],
                  ].map(([value, label]) => (
                    <label key={value} className="inline-flex cursor-pointer items-center gap-1.5">
                      <input
                        type="radio"
                        name="sms-delivery-mode"
                        checked={smsSettings.delivery_mode === value}
                        disabled={smsSettingsLoading || smsModeSaving}
                        onChange={() => handleSmsModeChange(value)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <p className="cms-sms-field-label text-xs text-neutral-500">
                  예약은 설정된 발송 시각에 전송되며, 그 시각 이후 게시물은 다음 날 발송됩니다.
                </p>

                <div className="grid gap-4">
                  <label className="grid gap-1.5">
                    <span className="cms-sms-field-label text-xs font-medium text-neutral-600">제목</span>
                    <input
                      type="text"
                      value={selectedSmsTemplate?.title || ""}
                      readOnly
                      placeholder="발송 문구 미선택"
                      className="h-10 rounded-md border border-neutral-200 bg-neutral-50 px-3 text-sm text-neutral-900 outline-none"
                    />
                  </label>
                  <label className="grid gap-1.5">
                    <span className="cms-sms-field-label text-xs font-medium text-neutral-600">내용</span>
                    <textarea
                      value={selectedSmsTemplate?.content || ""}
                      readOnly
                      placeholder="+ 불러오기에서 발송할 기본 문구를 선택해 주세요."
                      className="min-h-[320px] resize-y rounded-md border border-neutral-200 bg-neutral-50 px-3 py-3 text-sm leading-6 text-neutral-900 outline-none"
                    />
                  </label>
                </div>

                <div>
                  <div className="cms-sms-field-label mb-2 text-xs font-medium text-neutral-600">
                    MMS 콘텐츠 썸네일
                  </div>
                  <div className="flex min-h-[160px] items-center justify-center rounded-xl border border-neutral-200 bg-neutral-50 p-3">
                    {smsPreviewUsesVideo ? (
                      <video
                        src={smsVideoPreviewUrl}
                        muted
                        preload="metadata"
                        className="h-36 w-36 rounded-lg bg-black object-cover"
                      />
                    ) : smsImagePreviewUrl ? (
                      <img
                        src={smsImagePreviewUrl}
                        alt="MMS 콘텐츠 썸네일"
                        className="h-36 w-36 rounded-lg object-cover"
                      />
                    ) : (
                      <span className="text-xs text-neutral-400">
                        표시할 신청 이미지 또는 동영상이 없습니다.
                      </span>
                    )}
                  </div>
                  <div className="mt-2 text-[11px] text-neutral-400">
                    실제 발송 시에는 업로드된 게시물의 첫 이미지 또는 릴스 썸네일이 첨부됩니다.
                  </div>
                </div>
              </div>

              {smsModalOpen && (
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-label="SMS 기본 문구 불러오기"
                  className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-5"
                  onClick={handleCloseSmsModal}
                >
                  <div
                    className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
                      <div>
                        <div className="text-base font-semibold text-neutral-900">
                          SMS 기본 문구 불러오기
                        </div>
                        <div className="mt-1 text-xs text-neutral-500">
                          수정 내용은 모든 컨시어지 매장에 공통 적용됩니다.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleCloseSmsModal}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100"
                        aria-label="SMS 문구 팝업 닫기"
                      >
                        X
                      </button>
                    </div>

                    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
                      <label className="grid gap-1.5">
                        <span className="cms-sms-field-label text-xs font-medium text-neutral-600">기본 문구</span>
                        <select
                          value={smsDraftCode}
                          onChange={(event) => handleChangeSmsDraftTemplate(event.target.value)}
                          className="h-10 rounded-md border border-neutral-200 bg-white px-3 text-sm text-neutral-900 outline-none focus:border-violet-500"
                        >
                          {smsSettings.templates.map((template) => (
                            <option key={template.template_code} value={template.template_code}>
                              {template.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="grid gap-1.5">
                        <span className="cms-sms-field-label text-xs font-medium text-neutral-600">제목</span>
                        <input
                          type="text"
                          value={smsDraftTitle}
                          maxLength={100}
                          onChange={(event) => setSmsDraftTitle(event.target.value)}
                          className="h-10 rounded-md border border-neutral-200 bg-white px-3 text-sm text-neutral-900 outline-none focus:border-violet-500"
                        />
                      </label>

                      <label className="grid gap-1.5">
                        <span className="cms-sms-field-label text-xs font-medium text-neutral-600">내용</span>
                        <textarea
                          value={smsDraftContent}
                          maxLength={4000}
                          onChange={(event) => setSmsDraftContent(event.target.value)}
                          className="min-h-[390px] resize-y rounded-md border border-neutral-200 bg-white px-3 py-3 text-sm leading-6 text-neutral-900 outline-none focus:border-violet-500"
                        />
                        <span className="text-right text-[11px] text-neutral-400">
                          {smsDraftContent.length}/4000
                        </span>
                      </label>
                    </div>

                    <div className="flex items-center justify-between gap-3 border-t border-neutral-200 px-6 py-4">
                      <div className="text-xs text-neutral-500">
                        {smsDraftDirty
                          ? "수정한 문구를 저장한 후 선택할 수 있습니다."
                          : "선택하면 이 매장의 다음 업로드 완료 문자부터 적용됩니다."}
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <button
                          type="button"
                          onClick={handleCloseSmsModal}
                          disabled={smsTemplateSaving || smsTemplateSelecting}
                          className="h-10 rounded-md border border-neutral-300 px-4 text-sm text-neutral-700 hover:bg-neutral-100 disabled:opacity-50"
                        >
                          취소
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveSmsTemplate}
                          disabled={!smsDraftDirty || smsTemplateSaving || smsTemplateSelecting}
                          className="h-10 rounded-md bg-emerald-600 px-4 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {smsTemplateSaving ? "저장 중..." : "수정 저장"}
                        </button>
                        <button
                          type="button"
                          onClick={handleSelectSmsTemplate}
                          disabled={smsDraftDirty || smsTemplateSaving || smsTemplateSelecting}
                          className="h-10 rounded-md bg-violet-600 px-5 text-sm font-medium text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {smsTemplateSelecting ? "선택 중..." : "선택"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {previewImage && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label="신청 이미지 원본 보기"
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-6"
              onClick={() => setPreviewImage(null)}
            >
              <div
                className="relative flex max-h-full max-w-full items-center justify-center"
                onClick={(event) => event.stopPropagation()}
              >
                <img
                  src={previewImage.url}
                  alt={previewImage.alt}
                  className="h-auto max-h-[calc(100vh-3rem)] w-auto max-w-[calc(100vw-3rem)] object-contain"
                />
                <button
                  type="button"
                  onClick={() => setPreviewImage(null)}
                  className="absolute -right-3 -top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white text-lg font-semibold text-neutral-900 shadow-lg hover:bg-neutral-100"
                  aria-label="원본 이미지 닫기"
                >
                  X
                </button>
              </div>
            </div>
          )}

          {previewVideo && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label="신청 동영상 원본 보기"
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-6"
              onClick={() => setPreviewVideo(null)}
            >
              <div
                className="relative flex max-h-full max-w-full items-center justify-center"
                onClick={(event) => event.stopPropagation()}
              >
                <video
                  src={previewVideo.url}
                  controls
                  autoPlay
                  className="h-auto max-h-[calc(100vh-3rem)] w-auto max-w-[calc(100vw-3rem)] object-contain"
                />
                <button
                  type="button"
                  onClick={() => setPreviewVideo(null)}
                  className="absolute -right-3 -top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white text-lg font-semibold text-neutral-900 shadow-lg hover:bg-neutral-100"
                  aria-label="원본 동영상 닫기"
                >
                  X
                </button>
              </div>
            </div>
          )}

          <section className="pt-8">
            {updateError && <div className="mb-3 text-xs text-red-600">{updateError}</div>}

            <div className="cms-concierge-action-bar fixed inset-x-0 bottom-0 z-50 flex justify-center gap-3 border-t border-neutral-200 bg-white/95 px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur-sm">
              <button
                type="button"
                onClick={handleBackToList}
                disabled={
                  manualUploadLoading ||
                  updateLoading ||
                  terminateLoading ||
                  stopLoading ||
                  restartLoading
                }
                className="min-w-[96px] h-10 px-4 text-sm font-medium border border-neutral-300 text-neutral-700 hover:bg-neutral-100"
              >
                목록
              </button>

              <button
                type="button"
                onClick={() => handleSaveSelected({ approve: false })}
                disabled={
                  manualUploadLoading ||
                  updateLoading ||
                  terminateLoading ||
                  stopLoading ||
                  restartLoading
                }
                className={`min-w-[120px] h-10 px-4 text-sm font-medium text-white ${
                  updateLoading
                    ? "bg-emerald-400 cursor-not-allowed opacity-70"
                    : "bg-emerald-600 hover:bg-emerald-700"
                }`}
              >
                {updateLoading ? "수정 처리 중..." : "수정"}
              </button>

              {!userMode && (
                <button
                  type="button"
                  onClick={handleManualUpload}
                  disabled={
                    manualUploadLoading ||
                    manualUploadQueued ||
                    updateLoading ||
                    terminateLoading ||
                    stopLoading ||
                    restartLoading
                  }
                  className="inline-flex min-w-[148px] h-10 items-center justify-center gap-2 border border-neutral-300 bg-[#FFFFFF] px-4 text-sm font-medium text-[#111827] hover:bg-[#F3F4F6] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {["SUBMITTING", "CHECKING", "QUEUED", "RUNNING"].includes(manualUpload.phase) && (
                    <span
                      aria-hidden="true"
                      className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-900"
                    />
                  )}
                  <span>
                    {manualUpload.blocked ? MANUAL_UPLOAD_LABELS[manualUpload.phase] : "수동업로드"}
                  </span>
                </button>
              )}

              {!userMode && manualUpload.blocked && manualUpload.phase !== "SUBMITTING" && (
                <button type="button" onClick={manualUpload.recheck} disabled={manualUpload.checking}
                  className="h-10 border border-neutral-300 px-4 text-sm disabled:opacity-50">
                  {manualUpload.checking ? "상태 조회 중..." : "업로드 상태 재조회"}
                </button>
              )}

              {isApprovedStatus && (
                <button
                  type="button"
                  onClick={handleTerminateSelected}
                  disabled={
                    manualUploadLoading ||
                    updateLoading ||
                    terminateLoading ||
                    stopLoading ||
                    restartLoading
                  }
                  className={`min-w-[96px] h-10 px-4 text-sm font-medium text-white ${
                    manualUploadLoading ||
                    updateLoading ||
                    terminateLoading ||
                    stopLoading ||
                    restartLoading
                      ? "bg-neutral-400 cursor-not-allowed opacity-70"
                      : "bg-neutral-700 hover:bg-neutral-800"
                  }`}
                >
                  {terminateLoading ? "종료 처리 중..." : "종료"}
                </button>
              )}

              {isApprovedStatus && (
                <button
                  type="button"
                  onClick={handleStopSelected}
                  disabled={
                    manualUploadLoading ||
                    updateLoading ||
                    terminateLoading ||
                    stopLoading ||
                    restartLoading
                  }
                  className={`min-w-[96px] h-10 px-4 text-sm font-medium text-white ${
                    manualUploadLoading ||
                    updateLoading ||
                    terminateLoading ||
                    stopLoading ||
                    restartLoading
                      ? "bg-red-300 cursor-not-allowed opacity-70"
                      : "bg-red-500 hover:bg-red-600"
                  }`}
                >
                  {stopLoading ? "일시정지 처리 중..." : "일시정지"}
                </button>
              )}

              {!userMode && !isApprovedStatus && !isInactiveStatus && (
                <button
                  type="button"
                  onClick={() => handleSaveSelected({ approve: true })}
                  disabled={
                    manualUploadLoading ||
                    updateLoading ||
                    terminateLoading ||
                    stopLoading ||
                    restartLoading
                  }
                  className={`min-w-[120px] h-10 px-4 text-sm font-medium text-white ${
                    updateLoading
                      ? "bg-blue-300 cursor-not-allowed opacity-70"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {updateLoading ? "승인 처리 중..." : "승인"}
                </button>
              )}

              {isInactiveStatus && (
                <button
                  type="button"
                  onClick={handleRestartSelected}
                  disabled={
                    manualUploadLoading ||
                    updateLoading ||
                    terminateLoading ||
                    stopLoading ||
                    restartLoading
                  }
                  className={`min-w-[96px] h-10 px-4 text-sm font-medium text-white ${
                    manualUploadLoading ||
                    updateLoading ||
                    terminateLoading ||
                    stopLoading ||
                    restartLoading
                      ? "bg-blue-300 cursor-not-allowed opacity-70"
                      : "bg-blue-500 hover:bg-blue-600"
                  }`}
                >
                  {restartLoading ? "재시작 처리 중..." : "재시작"}
                </button>
              )}
            </div>
          </section>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileInputChange}
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4,.mp4"
            multiple
            className="hidden"
            onChange={handleVideoInputChange}
          />
        </main>
      </div>
    </div>
  );
}
